import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';

import Button from '../../components/ui/Button';
import ReportMetrics from './components/ReportMetrics';
import ReportTemplateLibrary from './components/ReportTemplateLibrary';
import InteractiveReportBuilder from './components/InteractiveReportBuilder';
import ScheduledExportQueue from './components/ScheduledExportQueue';
import ReportCategoryTabs from './components/ReportCategoryTabs';
import ExportFormatSelector from './components/ExportFormatSelector';

const ComprehensiveReportsCenter = () => {
  const [activeCategory, setActiveCategory] = useState('financial');
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showBuilder, setShowBuilder] = useState(false);
  const [processingReports, setProcessingReports] = useState([]);

  // Mock metrics data
  const metricsData = {
    totalScheduled: 47,
    successfulExports: 42,
    pendingGenerations: 5,
    storageUtilization: 68.5
  };

  // Simulate real-time processing
  useEffect(() => {
    const interval = setInterval(() => {
      setProcessingReports(prev => {
        if (prev?.length > 0) {
          return prev?.map(report => ({
            ...report,
            progress: Math.min(report?.progress + Math.random() * 10, 100)
          }));
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleGenerateReport = (template) => {
    const newReport = {
      id: Date.now(),
      name: template?.name,
      progress: 0,
      status: 'processing'
    };
    setProcessingReports(prev => [...prev, newReport]);
  };

  const handleExport = (format) => {
    console.log(`Exporting report in ${format} format`);
    // Export logic will be handled by individual components
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Comprehensive Reports Center
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Generate, schedule, and manage analytical reports across all data domains
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowBuilder(true)}
              >
                New Report
              </Button>
            </div>
          </div>
        </div>

        {/* Report Category Tabs & Controls */}
        <div className="mb-6">
          <ReportCategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        {/* Export Format Selector */}
        <div className="mb-6">
          <ExportFormatSelector
            selectedFormat={selectedFormat}
            onFormatChange={setSelectedFormat}
          />
        </div>

        {/* Metrics Dashboard */}
        <div className="mb-6">
          <ReportMetrics metrics={metricsData} processingReports={processingReports} />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Report Template Library - 12 cols on desktop */}
          <div className="lg:col-span-8">
            <ReportTemplateLibrary
              category={activeCategory}
              onGenerateReport={handleGenerateReport}
              selectedFormat={selectedFormat}
            />
          </div>

          {/* Scheduled Export Queue - 4 cols on desktop */}
          <div className="lg:col-span-4">
            <ScheduledExportQueue
              processingReports={processingReports}
              onExport={handleExport}
            />
          </div>
        </div>

        {/* Interactive Report Builder Modal */}
        {showBuilder && (
          <InteractiveReportBuilder
            onClose={() => setShowBuilder(false)}
            onGenerate={handleGenerateReport}
            selectedFormat={selectedFormat}
          />
        )}
      </div>
    </div>
  );
};

export default ComprehensiveReportsCenter;